import { useEffect, useRef } from 'react'
import { InView } from 'react-intersection-observer'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { TurboPickupCloseIcon } from 'src/components/Icons/TurboPickupCloseIcon'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'

import { TurboPickupStoreList } from './TurboPickupStoreList'
import { TurboPickupZipInput } from './TurboPickupZipInput'
import { useTurboPickup } from './useTurboPickup'

type TurboPickupModalProps = {
  onClose: () => void
}

export function TurboPickupModal({ onClose }: TurboPickupModalProps) {
  const { pageData } = useGlobalContext()
  const {
    postalCode,
    setPostalCode,
    modalState,
    stores,
    error,
    fetchStores,
    selectStore,
    setModalState,
  } = useTurboPickup()

  const hasTrackedCepViewedRef = useRef(false)
  const standardObject = getStandardObject(pageData) ?? {}

  const dispatchFastBuyEvent = (
    eventName: string,
    eventData: Record<string, string> = {}
  ) => {
    dispatchAmplitudeEvent({
      eventName,
      eventData: {
        ...standardObject,
        ...eventData,
      },
    })
  }

  useEffect(() => {
    if (postalCode) {
      fetchStores(postalCode)
    }
    // Run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = async (zipCode: string) => {
    setPostalCode(zipCode)

    const result = await fetchStores(zipCode)

    dispatchFastBuyEvent('Fast Buy CEP Status', {
      'fastbuy cep status': result.status === 'success' ? 'Sucesso' : 'Erro',
    })
  }

  const handleZipValidationError = () => {
    dispatchFastBuyEvent('Fast Buy CEP Status', {
      'fastbuy cep status': 'Erro',
    })
  }

  const handleSearchButtonClick = () => {
    dispatchFastBuyEvent('Fast Buy CEP Clicked')
  }

  const handleChangeZip = () => {
    setModalState('zip-input')
  }

  const handleSelect = (store: Parameters<typeof selectStore>[0]) => {
    dispatchFastBuyEvent('Fast Buy Store Selected', {
      'fastbuy store': store.pickupName,
    })

    setModalState('loading-products-store')
    selectStore(store)
  }

  const handleZipAreaInViewChange = (inView: boolean) => {
    if (!inView || hasTrackedCepViewedRef.current) {
      return
    }

    dispatchFastBuyEvent('Fast Buy CEP Viewed')
    hasTrackedCepViewedRef.current = true
  }

  return (
    <div className="flex flex-col gap-4 p-6 h-full min-h-0">
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between">
          <h2 className="text-[20px] font-semibold text-turboPickupTitle tracking-[-0.55px] leading-[30px] restructure-small-desktop:text-[22px] restructure-small-desktop:leading-[33px]">
            Retirada Turbo
          </h2>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="p-1 text-turboPickupBody hover:text-turboPickupTitle transition-colors shrink-0"
          >
            <TurboPickupCloseIcon aria-hidden="true" />
          </button>
        </div>
        <p className="text-[14px] text-turboPickupBody leading-[22.75px]">
          Compre online e retire seu pedido mais rápido em uma loja Decathlon
          perto de você. Encontre a unidade mais próxima e escolha onde fazer a
          retirada.
        </p>
      </div>

      {modalState === 'zip-input' && (
        <InView
          as="div"
          className="flex flex-col gap-4"
          triggerOnce
          threshold={0.5}
          onChange={handleZipAreaInViewChange}
        >
          <TurboPickupZipInput
            onSearch={handleSearch}
            onSearchButtonClick={handleSearchButtonClick}
            onValidationError={handleZipValidationError}
          />
          <div className="flex items-center justify-center border border-dashed border-turboPickupBorder rounded-[16px] h-[105px] mb-20 restructure-small-desktop:mb-0">
            <p className="text-[13px] text-turboPickupMuted text-center leading-[19.5px] px-4">
              Informe seu CEP para ver as lojas com retirada disponível.
            </p>
          </div>
        </InView>
      )}

      {(modalState === 'loading' ||
        modalState === 'loading-products-store') && (
        <div
          className={`flex flex-col items-center justify-center py-10 ${
            modalState === 'loading-products-store' ? 'md:h-2/3' : ''
          }`}
        >
          <span
            className="inline-block w-8 h-8 border-4 border-primaryBlue border-t-transparent rounded-full animate-spin"
            aria-label="Carregando lojas..."
            role="status"
          />
          {modalState === 'loading-products-store' && (
            <p className="text-[12px] font-medium text-turboPickupBody leading-[22.75px] mt-4 text-center">
              Estamos preparando os produtos disponíveis para retirada nesta
              loja.
            </p>
          )}
        </div>
      )}

      {(modalState === 'store-list' || modalState === 'error') && (
        <TurboPickupStoreList
          stores={stores}
          zipCode={postalCode ?? ''}
          onSelect={handleSelect}
          onChangeZip={handleChangeZip}
          error={error}
        />
      )}
    </div>
  )
}
